import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { has } from 'underscore';
import { DestinationPlatform } from 'blocks/DestinationPlatforms';
import { SubscriptionItem } from 'redux/middleware/api/podcast-service';
import { showNotification } from 'redux/modules/notification';
import {
  blueskyUsernameSelector,
  getFacebookPages,
  getInstagramAccounts as getInstagramUserAccounts,
  getTikTokCreatorInfo,
  getYoutubeChannels,
  threadsUserNameSelector,
  tiktokUserNameSelector,
  twitterUsernameSelector,
  waitForBlueskyProfile,
  waitForInstagramUser,
  waitForLinkedinProfile,
  waitForThreadsUser,
  waitForTiktokProfile,
  waitForTwitterProfile,
  youtubeChannelSelector,
} from 'redux/modules/social';
import {
  createFacebookDisplayNameSelector,
  createInstagramDisplayNameSelector,
  createLinkedinDisplayNameSelector,
} from './selectors';
import { HandleGetInfoErrorArgs } from './types';
import { mapYoutubeChannelName } from './utils';

export interface UseGetUserInfo {
  displayName: string;
}

export default function useGetUserInfo(
  subscriptionItem: SubscriptionItem,
): UseGetUserInfo {
  const { options } = subscriptionItem?.autoPostVideoPreference;

  const dispatch = useDispatch();
  const youtubeChannelData = useSelector(youtubeChannelSelector);
  const youtubeChannelName = mapYoutubeChannelName(youtubeChannelData);
  const tikTokAccountName = useSelector(tiktokUserNameSelector);
  const twitterUserName = useSelector(twitterUsernameSelector);
  const theadsUserName = useSelector(threadsUserNameSelector);
  const blueskyUserName = useSelector(blueskyUsernameSelector);
  const instagramDisplayName = useSelector(
    createInstagramDisplayNameSelector(options),
  );
  const linkedinDisplayName = useSelector(
    createLinkedinDisplayNameSelector(options),
  );
  const facebookDisplayName = useSelector(
    createFacebookDisplayNameSelector(options),
  );

  const platform = useMemo(
    (): DestinationPlatform =>
      subscriptionItem?.autoPostVideoPreference?.platform,
    [subscriptionItem],
  );

  const handleGetInfoError = useCallback(
    ({ title, message }: HandleGetInfoErrorArgs): void => {
      dispatch(
        showNotification({
          message,
          level: 'error',
          dismissAfterSec: 5,
          code: 'ER013',
          title,
          type: 'help',
          actionLabel: 'contact us',
        }),
      );
    },
    [dispatch],
  );

  const getYoutubeChannelName = useCallback(async (): Promise<void> => {
    try {
      await dispatch(
        getYoutubeChannels(
          subscriptionItem?.autoPostVideoPreference.options.googleId,
        ),
      );
    } catch {
      handleGetInfoError({
        title: "You won't be able to see your output YouTube channel right now",
        message: "Sorry, we couldn't load the YouTube channel name",
      });
    }
  }, [dispatch, handleGetInfoError, subscriptionItem]);

  const getTikTokAccountName = useCallback(async (): Promise<void> => {
    try {
      const tikTokUserId =
        subscriptionItem?.autoPostVideoPreference.options.tiktokUserId;

      await dispatch(waitForTiktokProfile(tikTokUserId));
      await dispatch(getTikTokCreatorInfo(tikTokUserId));
    } catch {
      handleGetInfoError({
        title: "You won't be able to see your output TitTok right now",
        message: "Sorry, we couldn't load the TikTok account name",
      });
    }
  }, [dispatch, handleGetInfoError, subscriptionItem]);

  const getInstagramAccounts = useCallback(async (): Promise<void> => {
    const instagramUserId =
      subscriptionItem?.autoPostVideoPreference.options.facebookId;

    try {
      await dispatch(getInstagramUserAccounts(instagramUserId));
    } catch {
      handleGetInfoError({
        title: "You won't be able to see your output Instagram right now",
        message: "Sorry, we couldn't load the Instagram account name",
      });
    }
  }, [dispatch, handleGetInfoError, subscriptionItem]);

  const getInstagramUser = useCallback(async (): Promise<void> => {
    const instagramUserId =
      subscriptionItem?.autoPostVideoPreference.options?.instagramUserId;

    try {
      await dispatch(waitForInstagramUser(instagramUserId));
    } catch {
      handleGetInfoError({
        title: "You won't be able to see your output Instagram right now",
        message: "Sorry, we couldn't load the Instagram account name",
      });
    }
  }, [dispatch, handleGetInfoError, subscriptionItem]);

  const getLinkedinUser = useCallback(async (): Promise<void> => {
    const linkedinUserId =
      subscriptionItem?.autoPostVideoPreference.options?.linkedinUserId;

    try {
      await dispatch(waitForLinkedinProfile(linkedinUserId));
    } catch {
      handleGetInfoError({
        title: "You won't be able to see your output LinkedIn right now",
        message: "Sorry, we couldn't load the LinkedIn account name",
      });
    }
  }, [dispatch, handleGetInfoError, subscriptionItem]);

  const getFacebookAccounts = useCallback(async (): Promise<void> => {
    const facebookId =
      subscriptionItem?.autoPostVideoPreference.options.facebookId;

    try {
      await dispatch(getFacebookPages(facebookId));
    } catch {
      handleGetInfoError({
        title: "You won't be able to see your output Facebook right now",
        message: "Sorry, we couldn't load the Facebook account name",
      });
    }
  }, [dispatch, handleGetInfoError, subscriptionItem]);

  const getTwitterUserName = useCallback(async (): Promise<void> => {
    try {
      const twitterUserId =
        subscriptionItem?.autoPostVideoPreference.options.twitterUserId;

      await dispatch(waitForTwitterProfile(twitterUserId));
    } catch {
      handleGetInfoError({
        title: "You won't be able to see your output X right now",
        message: "Sorry, we couldn't load the X user name",
      });
    }
  }, [dispatch, handleGetInfoError, subscriptionItem]);

  const getThreadsUserName = useCallback(async (): Promise<void> => {
    try {
      const threadsUserId =
        subscriptionItem?.autoPostVideoPreference.options.threadsUserId;

      await dispatch(waitForThreadsUser(threadsUserId));
    } catch {
      handleGetInfoError({
        title: "You won't be able to see your output Threads right now",
        message: "Sorry, we couldn't load the Threads user name",
      });
    }
  }, [dispatch, handleGetInfoError, subscriptionItem]);

  const getBlueskyUserName = useCallback(async (): Promise<void> => {
    try {
      const blueskyDid =
        subscriptionItem?.autoPostVideoPreference.options.blueskyDid;

      await dispatch(waitForBlueskyProfile(blueskyDid));
    } catch {
      handleGetInfoError({
        title: "You won't be able to see your output Bluesky right now",
        message: "Sorry, we couldn't load the Bluesky user name",
      });
    }
  }, [dispatch, handleGetInfoError, subscriptionItem]);

  const getInfoByPlatorm = useMemo((): Partial<
    Record<DestinationPlatform, () => void>
  > => {
    const { scopeContext } = subscriptionItem?.autoPostVideoPreference.options;

    return {
      youtube: getYoutubeChannelName,
      tiktok: getTikTokAccountName,
      instagram:
        scopeContext === 'instagramBusinessManageV2'
          ? getInstagramUser
          : getInstagramAccounts,
      linkedin: getLinkedinUser,
      facebook: getFacebookAccounts,
      twitter: getTwitterUserName,
      threads: getThreadsUserName,
      bluesky: getBlueskyUserName,
    };
  }, [
    getFacebookAccounts,
    getInstagramAccounts,
    getInstagramUser,
    getLinkedinUser,
    getThreadsUserName,
    getBlueskyUserName,
    getTikTokAccountName,
    getTwitterUserName,
    getYoutubeChannelName,
    subscriptionItem,
  ]);

  const displayNameByPlatform = useMemo((): Partial<
    Record<DestinationPlatform, string>
  > => {
    return {
      youtube: youtubeChannelName,
      tiktok: tikTokAccountName,
      instagram: instagramDisplayName,
      linkedin: linkedinDisplayName,
      facebook: facebookDisplayName,
      twitter: twitterUserName,
      threads: theadsUserName,
      bluesky: blueskyUserName,
    };
  }, [
    blueskyUserName,
    facebookDisplayName,
    instagramDisplayName,
    linkedinDisplayName,
    theadsUserName,
    tikTokAccountName,
    twitterUserName,
    youtubeChannelName,
  ]);

  useEffect(() => {
    if (has(getInfoByPlatorm, platform)) {
      getInfoByPlatorm[platform]();
    }
  }, [getInfoByPlatorm, platform]);

  return {
    displayName: displayNameByPlatform?.[platform] || '',
  };
}
